import React, { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Typography, Paper, Box } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

const navy = '#1A3A6B';

function descendingComparator(a, b, orderBy) {
  const aVal = getField(a, orderBy);
  const bVal = getField(b, orderBy);
  if (bVal < aVal) return -1;
  if (bVal > aVal) return 1;
  return 0;
}

function getField(row, key) {
  switch (key) {
    case 'eventName':    return row.eventTypesEntity.name;
    case 'eventYear':    return row.eventTypesEntity.eventYear;
    case 'status':       return row.status;
    case 'memberName':   return row.memberEntity.fullName;
    case 'quantities':   return row.quantities;
    case 'createdAt':    return row.transactionEntity.createdAt;
    default:             return '';
  }
}

const COLUMNS = [
  { id: 'eventName',  label: 'Event Name' },
  { id: 'eventYear',  label: 'Event Year' },
  { id: 'status',     label: 'Status' },
  { id: 'memberName', label: 'Member Name' },
  { id: 'quantities', label: 'Qty' },
  { id: 'createdAt',  label: 'Transaction Date' },
];

export default function EventTxnsHistory() {
  const { accessToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const [eventTxnsHistory, setEventTxnsHistory] = useState([]);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (accessToken) eventTxnsHistoryFetch();
  }, [accessToken]);

  useEffect(() => {
    if (tableContainerRef.current) tableContainerRef.current.scrollLeft = 0;
  }, [eventTxnsHistory]);

  const eventTxnsHistoryFetch = async () => {
    try {
      const response = await fetch(`${API_URL}/gquery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-cmdfw-jwt-token': `${accessToken}`,
        },
        body: JSON.stringify({
          query: `
            query {
              eventsHistory {
                events {
                  memEventsId
                  status
                  startDate
                  endDate
                  quantities
                  memberEntity { memberId memberType fullName }
                  eventTypesEntity { eventTypeId name eventYear price }
                  transactionEntity { transactionId totalAmount paymentStatus createdAt }
                }
              }
            }
          `,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setEventTxnsHistory(data.data.eventsHistory.events);
      }
    } catch (error) {
      console.error('Error fetching Event Txns History:', error);
    }
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const sortedRows = [...eventTxnsHistory].sort((a, b) =>
    order === 'desc'
      ? descendingComparator(a, b, orderBy)
      : -descendingComparator(a, b, orderBy)
  );

  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper elevation={3} sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ bgcolor: navy, px: 3, py: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'Georgia, serif' }}>
          Event Transactions History
        </Typography>
      </Box>

      <TableContainer ref={tableContainerRef} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }} aria-label="event transactions table">
          <TableHead>
            <TableRow sx={{ bgcolor: '#e4eaf0' }}>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}
                  sx={{ fontWeight: 700, color: navy, whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                    sx={{
                      '&.Mui-active': { color: navy },
                      '& .MuiTableSortLabel-icon': { color: `${navy} !important` },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#888' }}>
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, idx) => (
                <TableRow key={row.memEventsId ?? idx}
                  sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f0f4f8' } }}>
                  <TableCell>{row.eventTypesEntity.name}</TableCell>
                  <TableCell>{row.eventTypesEntity.eventYear}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.memberEntity.fullName}</TableCell>
                  <TableCell>{row.quantities}</TableCell>
                  <TableCell>{row.transactionEntity.createdAt}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={eventTxnsHistory.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Paper>
  );
}
